/**
 * The release history shown at /whats-new, and the version the account menu names.
 *
 * **One list, newest first.** Every web or app release adds its entry HERE, in the same pull request that
 * ships it (docs: `runbooks/deploy.md` "Release notes"). Versions are semantic (major.minor.patch): a new
 * first number is a big change, a new middle number adds things, the last number is a fix. The `date` is the
 * deploy day as a calendar day ("2026-10-17"), never an instant, so it is shown as written with `formatDay`
 * and no time zone can move it.
 *
 * It lives in `shared/` rather than in the releases module because the account menu (shared layout) and the
 * Help page (another module) both need `CURRENT_WEB_VERSION`, and a shared file may not import a module.
 *
 * **Writing an entry.** Plain, friendly portal language for the people who use the product: what they can do
 * now, not how it was built. No servers, backups, pipelines, migrations or operator-only tools, and nothing
 * the product cannot yet do.
 */
export type ReleasePlatform = "web" | "app";

export interface Release {
  platform: ReleasePlatform;
  /** Semantic version, "major.minor.patch". Unique within a platform. */
  version: string;
  /** The deploy day, "YYYY-MM-DD"; `null` while the release is not published yet (store review). */
  date: string | null;
  /** `in_review`: sent to the stores and not published; such an entry has no date. */
  status?: "in_review";
  /** A short name for the release, shown under its version. */
  title: string;
  summary?: string;
  new: readonly string[];
  improved: readonly string[];
  fixed: readonly string[];
}

export const RELEASES: readonly Release[] = [
  {
    platform: "web",
    version: "2.5.1",
    date: "2026-11-01",
    title: "15 days to change your mind",
    summary: "Deleting your account now takes effect after 15 days, so a mistake can be undone.",
    new: [],
    improved: [
      "When you delete your account, every device signs out and the account goes 15 days later. Sign in before then to cancel.",
    ],
    fixed: [],
  },
  {
    platform: "web",
    version: "2.5.0",
    date: "2026-11-01",
    title: "Delete your account",
    summary: "Anyone can now delete their NexBorg Access account themselves, in the app or on the web.",
    new: [
      "Delete your account in the app at More, then Delete account, or on the public account deletion page with a code sent to your mobile.",
      "Managers get a notice in the app when a member deletes their account.",
    ],
    improved: [
      "The audit log keeps the name of a person who deleted their account, marked as account deleted. Your organisation's own records, such as people and attendance, stay as they are.",
    ],
    fixed: [],
  },
  {
    platform: "web",
    version: "2.4.0",
    date: "2026-10-29",
    title: "Labour shifts and a daily attendance sheet",
    summary:
      "Run day and night shifts in Labour, each with its own hours, punch window and overtime limit, and see attendance shift by shift. A new daily sheet shows who came, in the order they arrived.",
    new: [
      "Labour settings → Shifts: add day and night shifts with a start and end time, how early and how late punches are accepted, standard hours, half day and the most overtime a day can hold. A night shift that ends the next morning is counted on the day it started.",
      "A labourer's shift is found from their first punch of the day, or you can fix a shift on their profile.",
      "New report: Shift-wise attendance. Pick one or more shifts and a day or range to see the headcount, present, late in, early out, hours and overtime per shift, with every labourer's day below. Excel and PDF.",
      "New report: Daily attendance. Every labourer's first in, last out, worked hours and day status (PR, HD, AB and more) in order of arrival, with search, grouping by contractor or department, and a choice of columns. Excel and PDF follow the columns you show.",
      "An Attendance filter (Everyone, Present, Absent) on the daily muster, hours, time sheet and the two new reports.",
    ],
    improved: [
      "Labour reports gain a Shift filter and a Shift column where each row is a day; the employee data sheet prints a labourer's fixed shift.",
      "Today splits each site's headcount by shift, including who came in late.",
      "Daily muster: search by labour number, name or mobile.",
    ],
    fixed: [],
  },
  {
    platform: "web",
    version: "2.3.0",
    date: "2026-10-28",
    title: "Move people already in People into Staff and Labour",
    summary:
      "People your terminal already knows can now become employees or labourers by import, without adding them a second time. Start from a file already filled with them.",
    new: [
      "Staff and Labour imports: download a file pre-filled from People. It lists everyone who is not an employee or a labourer yet, with their name, mobile, terminal user ID and reference. Fill in the Staff or Labour columns, delete anyone who does not belong, and import it back.",
      "Staff import: a row with a terminal user ID now finds that person in People and makes them an employee. Their name and mobile stay as they are, so the name can be left blank. A person without an employee number takes the one in the file, or the next number if you number employees automatically.",
      "Labour import: a new People reference column, and the Terminal user ID column now also finds a person already in People. That person becomes a labourer instead of being added again, and the check shows who each row found.",
    ],
    improved: [
      "Labour import: someone already on a site's terminals no longer counts against the terminal's room when they become a labourer at that site.",
      "Imports refuse a terminal user ID or reference that lost its leading zeros in Excel (12 where People has 0012) and name the person it probably meant, instead of adding someone new.",
    ],
    fixed: [],
  },
  {
    platform: "web",
    version: "2.2.0",
    date: "2026-10-23",
    title: "Fuller co-working receipts and a round of fixes",
    summary:
      "Co-working receipts now show who paid, what each payment settled and what is still due. A list of fixes from the team's review of Payroll, Co-working, Visitors and Face Kiosk.",
    new: [
      "Co-working: the receipt PDF now prints the payer's address, state, GSTIN and PAN where known, and for each invoice it settles the date, description or period, the invoice total, the amount applied and the balance after this receipt, with an authorised-signatory line.",
      "API: a person's cards can now be listed directly (GET /people/{id}/cards), beside adding and removing them.",
    ],
    improved: [
      "Payroll: File for an employee tells you up front when the tax year is not published yet, instead of failing on Open declaration.",
      "Settings: the custom-role permission grid wraps long column names instead of overlapping them.",
      "Co-working settings: the Signature card has a proper Choose image button, and the GST registrations table no longer wraps the state or spills out of its card.",
      "Co-working: the filters on Payments, Deposits and TDS line up on one row.",
    ],
    fixed: [
      "Co-working: New contract → Who: the “An individual member” tab can be chosen again, so a contract can be made for a member.",
      "Payroll settings: the payslip logo shows in the preview after upload.",
      "Payroll settings: a pay component’s Valid from and Valid to month can be picked; before, the first pick was cleared.",
      "Payroll: on a narrow phone the employee page’s Payslips and Deductions tabs can be reached by scrolling the tab row.",
      "Visitors: the “One entry every” dropdown draws its arrow inside the field.",
      "Visitors: deleting a visitor returns to the list instead of showing “Not found”.",
      "Face Kiosk settings and the People list no longer leave a blank area under the page.",
      "Creating an organisation no longer shows “This page is out of date” right after the organisation is made.",
    ],
  },
  {
    platform: "web",
    version: "2.1.0",
    date: "2026-10-18",
    title: "Visitor passes as a link on the phone",
    summary:
      "Give a visitor their pass as a link. They open it on their own phone, confirm their mobile number, and show the QR code at the gate.",
    new: [
      "Visitors: give a visitor their pass as a link they open on their phone. They enter their mobile number to see the QR code, and that phone then opens straight to it until the pass ends. Five wrong numbers lock the link until the desk unlocks it.",
      "Visitors: copy the link or share it on WhatsApp straight from the pass. Sending it by SMS uses your SMS credits and becomes available once the text message is approved (DLT); until then, copy or share the link.",
      "Visitors: a new Visitor settings page. Switch pass links on, choose whether the visitor confirms their mobile number first, and whether the keypad PIN shows on the link page.",
      "Visitors: each visit shows its link, whether the text was sent and how often the link was opened. Send it again, unlock it after wrong numbers, or revoke it.",
    ],
    improved: [
      "Help centre: the Visitors guide explains pass links and what the visitor sees on their phone.",
    ],
    fixed: [],
  },
  {
    platform: "web",
    version: "2.0.1",
    date: "2026-10-17",
    title: "Switch Face Kiosk on from the setup guide",
    new: [],
    improved: [],
    fixed: [
      "Setup guide: the Face Kiosk phone tab now lets you switch Face Kiosk on right there, then create the seat. Before, it said Face Kiosk was off and showed an error, even when Face Kiosk was in your plan.",
    ],
  },
  {
    platform: "web",
    version: "2.0.0",
    date: "2026-10-17",
    title: "Gym overdue blocking, a help centre and many fixes",
    summary:
      "Gyms can stop the door for members who owe. There is now a help centre with a guide for every module, and a long list of fixes.",
    new: [
      "Gym: turn on overdue blocking and members who owe are stopped at the door. Only their gym-membership access is blocked, and it comes back by itself once they pay.",
      "Gym: choose how many days, from 0 to 90, a member can stay over the overdue amount before the door block starts.",
      "Gym: change the start date of a membership that has not started yet.",
      "Gym: bring back a plan you archived.",
      "Students: add a range of holidays in one go, up to 62 days at a time.",
      "Help centre: a guide for every module, with the steps to set it up and run it day to day. Each setup page now links to its guide.",
    ],
    improved: [
      "The public Terms of Service and Privacy Notice are clearer. They now give our contact details and the governing law, and the Privacy Notice has a table of what we keep and for how long.",
      "The account deletion page explains how we confirm a request before deleting anything.",
      "Deleting an organisation now reminds you to download your data export and to keep your tax records.",
      "Payroll warns you when PF, ESI or Professional Tax is switched on but no rule is in force yet, and a blocked payslip now links to Statutory settings so you can see why.",
      "Visitor passes show how many QR scans have been used, and say that opening the door with a PIN is not counted.",
      "Face Kiosk: you can filter scans by the reason they are waiting.",
      "Labour: the import template shows the Labour number column only when your organisation types labour numbers itself.",
      "Larger files now go through. Attachments, proof uploads, staff selfie punches and imports over 700 KB were being refused before.",
    ],
    fixed: [
      "Unlock methods are named correctly in events, filters and reports. A keypad PIN no longer shows as “Card + face”.",
      "A visitor who opens the door with their PIN, card or face is now checked in.",
      "Staff attendance: the list no longer shows a wrong “active” time on every row.",
      "Labour reports: after you clear the site, the page no longer keeps showing the previous site's report.",
      "Role cards no longer say “Can change” for roles that can only view and export.",
      "The “unsaved changes” prompt no longer keeps coming back after you press Cancel.",
      "Co-working: the CSV templates now show the ₹ sign correctly in their headings.",
      "Yearly plan prices now show correctly.",
      "The change-plan page now opens for people who can only view billing.",
      "Push notifications are more reliable: a failed one is tried again, and you no longer get the same one twice.",
    ],
  },
  {
    platform: "web",
    version: "1.3.0",
    date: "2026-10-11",
    title: "Co-working, Labour attendance, Payroll and Face Kiosk",
    summary:
      "Three new add-on workspaces and the first part of Face Kiosk. Each add-on appears in the workspace switcher once your organisation has it.",
    new: [
      "Co-working: sell seats and day passes to companies and individuals. Sign contracts, give each seat its door access, invoice in advance on the billing day, record payments (including tax deducted at source), and keep track of deposits, occupancy and revenue.",
      "Labour attendance: keep a register of labourers by contractor and site. Attendance comes from their punches at the gate, so nobody ticks names on a sheet. Get a daily muster, time sheets for each site and contractor, and a day file for your ERP.",
      "Payroll: price a month's salary for the people Staff lists as employees. Set up salary structures, add overtime, loans and claims, run the month, publish payslips, download one bank file and record the payments. Payroll works while Staff is active.",
      "Payroll: PF, ESI, Professional Tax and income tax follow rules that NexBorg publishes after they are checked. Until a rule is published for a month, a payslip that needs it is held back instead of being worked out with a guess.",
      "Staff: choose the day your pay month starts, such as the 26th. Staff attendance periods follow it, and Payroll uses the same months.",
      "Face Kiosk: set up seats for phones held at a gate, record people's consent and review the scans. It comes with Access Control. Scanning comes with the mobile app.",
    ],
    improved: [
      "The Staff CSV export now ends with two extra columns, period start and period end.",
      "Face photos stored for people you have deleted are now removed automatically each night.",
      "Gym: the overdue setting is now called “Flag members who owe”. It marks members who owe and does not close a door.",
    ],
    fixed: [],
  },
  {
    platform: "web",
    version: "1.2.1",
    date: "2026-10-07",
    title: "Face photos on more terminals",
    summary: "A quick fix for face photos on terminals that run the firmware most customers have.",
    new: [],
    improved: [],
    fixed: [
      "Face photos are now sent to the terminal directly, so terminals on the usual customer firmware accept them. Before, some of them refused every face.",
      "If a terminal refuses a face photo, the person's cards and fingerprints are still sent to it.",
      "Students: the weekdays you tick for a different timetable on a date range are now always saved in calendar order.",
    ],
  },
  {
    platform: "web",
    version: "1.2.0",
    date: "2026-10-06",
    title: "Staff In and Out terminals and gym attendance",
    summary: "Better time at work for Staff, and a closer look at who comes to the gym.",
    new: [
      "Staff: choose which terminals are In and which are Out, or let one terminal alternate between In and Out. Time at work is counted from each In to the next Out.",
      "Staff: flexible shifts, which count the hours required in a day instead of a fixed start and end.",
      "Staff: an Employee attendance report for one employee's month, day by day, that you can download as Excel or PDF. Choose and reorder its columns.",
      "Gym: a Visits strip on the dashboard shows this week and this month against the same point last time, the daily average, the busiest hour and the members not seen for 30 days.",
      "Gym: four new reports, Visits trend, Member attendance, Inactive members and Peak hours, with Excel, PDF and CSV files.",
    ],
    improved: ["Staff: punches less than 10 seconds apart count as one (it was 5 seconds)."],
    fixed: [],
  },
  {
    platform: "web",
    version: "1.1.0",
    date: "2026-10-05",
    title: "Renewal invoices that stay up to date",
    summary: "Renewal invoices follow price and plan changes, and discounts are easier to read.",
    new: [],
    improved: [
      "A renewal invoice that is waiting to be paid now stays up to date. If a discount, a price or a plan change affects the renewal, the old invoice is replaced by a new one with the right amount, for the same period and due date.",
      "Discounts show clearly on the Subscription page and on invoices: the list price struck out, the percentage, the name of the rule, and the total discount before GST.",
      "You stay signed in. The portal renews your sign-in on its own, a poor connection no longer sends you back to the sign-in page, and you are signed out only when your access is removed.",
      "Device page: its lists show ten rows at a time, and a terminal that needs setting up by hand has a dialog with the address, the ports and the menu pages to fill in.",
    ],
    fixed: [
      "A free trial of an add-on inside a paid subscription no longer appears on the renewal invoice.",
      "The nightly check that makes every terminal match the portal now runs at midnight (IST). Before, it ran at a time that depended on when the service last started.",
    ],
  },
  {
    platform: "web",
    version: "1.0.1",
    date: "2026-10-04",
    title: "Terminal sign-in fixes",
    summary: "An evening fix on the day of the first release.",
    new: [
      "Change the sign-in a terminal uses from its device page. If the password was changed on the terminal, enter the new one: there is no need to pair it again.",
    ],
    improved: [],
    fixed: [
      "Some terminals refuse the password the portal gives them for sending events, which can happen after a reset by the manufacturer. They now connect with a token instead and finish setting up on their own, and the device page says so.",
    ],
  },
  {
    platform: "web",
    version: "1.0.0",
    date: "2026-10-04",
    title: "NexBorg Access goes live",
    summary: "The first release of the web portal.",
    new: [
      "Sign in with your mobile number and a one-time code. Create an organisation or accept an invitation, and give each member a role.",
      "Pair face terminals, then watch their status and settings on the Devices page.",
      "Add people with a photo, card or PIN and send them to the terminals you choose. Group people into access groups, and set schedules and holidays.",
      "See entries as they happen in the live feed and the event log, and open a door from the portal.",
      "Attendance and other reports, with Excel and PDF files.",
      "Add-on workspaces for Gym, Visitors, Staff attendance and Student attendance.",
      "A Subscription page for your plan and add-ons, with free trials, discounts and invoices you can pay online.",
    ],
    improved: [],
    fixed: [],
  },
  {
    platform: "app",
    version: "1.1.0",
    date: "2026-10-24",
    title: "Visitor pass links, gym attendance and a round of fixes",
    summary:
      "Out on Google Play now; the App Store update follows its review. Visitor passes as a link on the phone, gym attendance, Staff in and out terminals, and fixes from the team's review.",
    new: [
      "Visitors: give a visitor their pass as a link they open on their phone.",
      "Gym: attendance on the dashboard and the four gym reports.",
      "Staff: in and out terminals, flexible shifts and the employee report.",
    ],
    improved: [],
    fixed: [
      "Labour: Take photo asks for the camera permission first instead of failing with a library message.",
      "Labour: the Attendance and Documents tab labels no longer break mid-word.",
      "Deleting a visitor, a person or a lead returns to the list without a “may have been removed” message.",
    ],
  },
  {
    platform: "app",
    version: "1.0.0",
    date: null,
    status: "in_review",
    title: "First release, in store review",
    summary:
      "The first version of the NexBorg Access app was sent to the App Store and Google Play for review on 12 Oct 2026. It will be listed here with its release date once it is published.",
    new: [],
    improved: [],
    fixed: [],
  },
];

/** One platform's releases, newest first. */
export function releasesFor(platform: ReleasePlatform): readonly Release[] {
  return RELEASES.filter((release) => release.platform === platform);
}

const newestWeb = releasesFor("web")[0];
if (!newestWeb) throw new Error("releases.ts must list at least one web release");

/** The version the portal is on: the newest web entry, so the account menu can never lag the history. */
export const CURRENT_WEB_VERSION: string = newestWeb.version;
